import React, { useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePacking from '../../utils/usePacking';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { Link, useParams } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import AlreadyPlacedMessage from './AlreadyPlacedMessage';
import AlreadyUnpackedMessage from './AlreadyUnpackedMessage';
import FinishedUnpackingDialog from './FinishedUnpackingDialog';
import Packing from '../../components/Packing';
import Page from '@afosto/app-components-react/Page';
import translations from './translations';

const UnpackingView = () => {
  usePrivate();

  const intl = useIntl();
  const { shipmentID } = useParams();
  const [hasPermissions, UnauthorizedComponent] = usePermissions([
    'lcs:shipments:read',
    'lcs:shipments:write',
    'lcs:handling:write',
  ]);
  const packingContext = usePacking(shipmentID, true);
  const { isFetchingShipment, shipment } = packingContext ?? {};
  const [showFinishedUnpackingDialog, setShowFinishedUnpackingDialog] = useState(false);

  const handleFinish = () => {
    setShowFinishedUnpackingDialog(true);
  };

  if (!hasPermissions()) {
    return (
      <Page title={shipment?.number ?? shipment.id}>
        <UnauthorizedComponent />
      </Page>
    );
  }

  const isShipmentHandled = shipment?.is_handled ?? false;
  const isShipmentUnpacked = shipment?.is_boxed ?? false;
  const isInvalidShipment =
    (!isFetchingShipment && !shipment) || (!!shipment && shipment?.is_outbound);
  const isValidShipment =
    !!shipment && !shipment?.is_outbound && !isShipmentHandled && !isShipmentUnpacked;
  const showAlreadyPlacedMessage = !isFetchingShipment && !!shipment && isShipmentHandled;
  const showAlreadyUnpackedMessage = !isFetchingShipment && !!shipment && isShipmentUnpacked;

  return (
    <>
      <Page
        isLoading={isFetchingShipment}
        title={shipment?.number ?? shipment?.id}
        breadcrumbs={
          shipment
            ? [
                {
                  label: intl.formatMessage(translations.breadcrumbs.shipments),
                  path: '/shipments',
                },
                {
                  label: shipment?.number ?? shipment?.id,
                  path: `/shipments/${shipmentID}`,
                },
                {
                  label: intl.formatMessage(translations.breadcrumbs.parcels),
                  path: `/shipments/${shipmentID}/parcels`,
                },
                {
                  label: shipment?.number ?? shipment?.id,
                  path: `/unpacking/${shipmentID}`,
                  active: true,
                },
              ]
            : []
        }
        linkComponent={Link}
        fullWidth
      >
        {isValidShipment && <Packing onFinish={handleFinish} packingContext={packingContext} />}
        {isInvalidShipment && <Typography variant="body2">Shipment not found</Typography>}
        {showAlreadyUnpackedMessage && <AlreadyUnpackedMessage shipment={shipment} />}
        {showAlreadyPlacedMessage && <AlreadyPlacedMessage shipment={shipment} />}
      </Page>
      <FinishedUnpackingDialog isOpen={showFinishedUnpackingDialog} shipment={shipment} />
    </>
  );
};

export default UnpackingView;
